import { ACTION_STATUSES, TRANSACTION_TYPES } from 'shared/consts';
import { BookingStatus } from 'store/bookings/types';
import { ReceiptContainer, StyledModalWrapper, LeftPaneContentContainer, StyledTwoColumnModal } from '../styles';
import React from 'react';
import { PatchTransaction, TransactionDetails } from 'store/transactions/types';
import { useIntl } from 'react-intl';
import { ReceiptOverview } from './receipt-overview';
import { ReceiptCheckIn } from './receipt-check-in';
import { ActionBarClearance, ReceiptTemperatureVote } from './styles';
import { OrderedAddOns, useOrderedAddOnsVisible } from 'components/ordered-add-ons';
import { ReceiptSummary } from './receipt-summary';
import { CancelBooking } from '../../cancel-booking';
import { useSelector } from 'react-redux';
import { selectTransactionStatus } from 'store/transactions/selectors';
import { useBookingObject } from 'components/payment/receipt/components/hooks/useBookingObject.hook';
import { OrderType } from 'store/cart/types';
import { DoneButtonBlock } from 'components/payment/receipt/components/done-button';
import { UnlockDorsLink } from 'components/payment/receipt/components/unlock-dors-link';
import { isBookingActionable } from 'components/payment/receipt/components/utils/booking-actionability.util';
import { useFullscreenModalStyles } from 'hooks/payment-content/use-fullscreen-modal-styles.hook';

interface ReceiptContentProps {
  transaction: TransactionDetails;
  onCloseClick: VoidFunction;
  onCancelBookingClick: VoidFunction;
  patchTransactionData: Pick<PatchTransaction, 'status' | 'type'>;
  isCancelBookingStep: boolean;
  setIsCancelBookingStep: React.Dispatch<React.SetStateAction<boolean>>;
  onCheckInClick: VoidFunction;
  isFullscreen?: boolean;
  /*
   * VIS-2032: the activity-centre pane is a persistent detail view, not a dismissible modal, so there
   * is nothing for a Done control to close. The swipe branch has honoured this since that ticket; this
   * one was never given the flag, so the desktop-width pane kept rendering the footer.
   */
  isActivityPane?: boolean;
}

export const ReceiptContent = ({
  onCancelBookingClick,
  onCloseClick,
  patchTransactionData,
  transaction,
  isCancelBookingStep,
  setIsCancelBookingStep,
  onCheckInClick,
  isFullscreen = false,
  isActivityPane = false,
}: ReceiptContentProps) => {
  const intl = useIntl();
  const getTransactionStatus = useSelector(selectTransactionStatus);
  // A past/terminal booking no longer offers check-in or cancel (see the util) — its transaction
  // status never changes after it ends, so gate on time as well as status.
  const bookingIsActionable = isBookingActionable(transaction);
  /* One decision, two uses: the receipt drops the vendor lines because this renders them. */
  const hasOrderedAddOns = useOrderedAddOnsVisible(transaction);
  // RES-1231: check-in must not be offered until the booking is confirmed (Accepted).
  const isCheckInAllowed =
    transaction?.details?.status === BookingStatus.ACCEPTED &&
    !!transaction?.details?.check_in?.status &&
    patchTransactionData.status !== ACTION_STATUSES.FULFILLED &&
    bookingIsActionable;
  const mappedBookingObject = useBookingObject(transaction);

  useFullscreenModalStyles(isFullscreen);

  return isCancelBookingStep ? (
    <StyledModalWrapper
      isCancelBookingPage
      visible
      content={<CancelBooking setIsCancelBookingStep={setIsCancelBookingStep} transaction={transaction} />}
    />
  ) : (
    <ReceiptContainer data-testid="receipt-page" isFullscreen={isFullscreen}>
      {transaction && (
        <StyledTwoColumnModal
          isContentLoading={getTransactionStatus === ACTION_STATUSES.PENDING}
          isVisible
          onClose={onCloseClick}
          isFullscreen={isFullscreen}
          LeftPaneContent={
            <LeftPaneContentContainer isFullscreen={isFullscreen}>
              <ReceiptOverview transaction={transaction} />
              {isCheckInAllowed && <ReceiptCheckIn onCheckIn={onCheckInClick} />}
              {!!transaction?.details?.resource_id && (
                <ReceiptTemperatureVote
                  resourceId={String(transaction.details.resource_id)}
                  startAt={transaction.details.start_at}
                  endAt={transaction.details.end_at}
                  checkInStatus={transaction.details.check_in?.status}
                />
              )}
              <UnlockDorsLink transaction={transaction} />
            </LeftPaneContentContainer>
          }
          RightPaneContent={
            <>
              <ReceiptSummary
                onCancelBooking={bookingIsActionable ? onCancelBookingClick : undefined}
                hideVendorAddOns={hasOrderedAddOns}
                removeBottomSpacing={hasOrderedAddOns || isActivityPane}
                transaction={mappedBookingObject}
              />
              {/*
                ⛔ Its own block, not the receipt's payment details. Vendor add-ons never enter
                `total_price`, and that block is gated on it -- so on a free room a $50 add-on
                appeared nowhere at all. See `OrderedAddOns`.
              */}
              <ActionBarClearance hasActionBar={!isActivityPane}>
                <OrderedAddOns transaction={transaction} />
              </ActionBarClearance>
            </>
          }
          Button={
            !isActivityPane &&
            (transaction?.type === TRANSACTION_TYPES.RESOURCES_BOOKING ||
              mappedBookingObject?.type === OrderType.RESOURCE_BOOKING) &&
            !mappedBookingObject.isMultiday && (
              <DoneButtonBlock onClick={onCloseClick} text={intl.formatMessage({ id: 'done' })} />
            )
          }
        />
      )}
    </ReceiptContainer>
  );
};
